import classNames from "classnames";
import React from "react";
import { Key } from "ts-key-enum";

import { useMountEffect } from "../../../hooks/useMountEffect";
import { IProduct } from "../../../models/catalogue";
import { BreakPoint } from "../../../models/screen";
import { getViewportBreakpoint } from "../../../utils/detectMobile";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { defaults } from "../defaults";
import { GridFilterConfig } from "../filters";
import { GridFilterSelectionMode, IFilterOption } from "../models";
import {
    updateFilterVisibility,
    updateSelectManyFilterValue,
    updateSelectOneFilterValue,
} from "../reducers";
import { IFilterState } from "../reducers.interfaces";
import { GridFilterOption } from "./GridFilterOption";

import styles from "./GridFilter.module.scss";

const buildSelectedValuesLabels = (
    filterOptions: IFilterOption[],
    filterValueState: IFilterState,
): string[] => {
    const labels: string[] = [];
    const selectedValues = new Set(
        filterValueState ? filterValueState.selectedValues : [],
    );
    for (const option of filterOptions) {
        if (selectedValues.has(option.id)) {
            labels.push(option.label);
        }
    }
    return labels;
};

const buildSelectOneFilterBody = (
    config: GridFilterConfig,
    options: IFilterOption[],
    selectedValues: readonly string[],
    products: readonly IProduct[],
    onSelectOneChange: (
        optionNamespace: string,
        filterID: string,
        optionID: string | null,
    ) => void,
): React.ReactNode => {
    const selectID = `grid-filter-${config.filterID}`;
    return (
        <div className={styles.sizeSelector}>
            <select
                id={selectID}
                name={selectID}
                className={styles.sizeSelectorSelect}
                value={selectedValues[0] || ""}
                onChange={(e) => {
                    onSelectOneChange(
                        products[0]?.product_class_slug,
                        config.filterID,
                        e.currentTarget.value,
                    );
                }}
            >
                <option value={""}>Select</option>
                {options.map((option) => (
                    <option key={option.id} value={option.id}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

const buildSelectManyFilterBody = (
    config: GridFilterConfig,
    options: IFilterOption[],
    onSelectManyChange: (
        optionNamespace: string,
        filterID: string,
        optionID: string,
        isSelected: boolean,
    ) => void,
): React.ReactNode => {
    return (
        <>
            {options.map((option) => (
                <GridFilterOption
                    key={option.id}
                    filterID={config.filterID}
                    option={option}
                    onChange={onSelectManyChange}
                />
            ))}
        </>
    );
};

const buildFilterBody = (
    config: GridFilterConfig,
    products: readonly IProduct[],
    selectedValues: readonly string[],
    onSelectOneChange: (
        optionNamespace: string,
        filterID: string,
        optionID: string | null,
    ) => void,
    onSelectManyChange: (
        optionNamespace: string,
        filterID: string,
        optionID: string,
        isSelected: boolean,
    ) => void,
): React.ReactNode => {
    const options = config.listFilterOptions(products);
    if (config.selectionMode === GridFilterSelectionMode.SELECT_ONE) {
        return buildSelectOneFilterBody(
            config,
            options,
            selectedValues,
            products,
            onSelectOneChange,
        );
    }
    return buildSelectManyFilterBody(config, options, onSelectManyChange);
};

export const GridFilter = React.memo(
    (props: {
        config: GridFilterConfig;
        showSelectedList: boolean;
    }): React.ReactNode => {
        const dispatch = useAppDispatch();
        const filterRef = React.useRef<HTMLDivElement>(null);
        const visibilityID = props.config.filterID;
        const bodyId = `filter-body-${props.config.filterID}`;

        const products = useAppSelector(
            (rootState) => (rootState.productgrid2 || defaults).products,
        );
        const filterState = useAppSelector(
            (rootState) =>
                (rootState.productgrid2 || defaults).filters[visibilityID],
        );
        const valueState = useAppSelector(
            (rootState) =>
                (rootState.productgrid2 || defaults).filters[
                    props.config.filterID
                ],
        );
        const options = props.config.listFilterOptions(products);
        const isOpen = filterState ? filterState.isOpen : true;
        const selectedValues = valueState ? valueState.selectedValues : [];
        const selectedValuesLabels = buildSelectedValuesLabels(
            options,
            valueState,
        );

        const onSetVisibility = (id: string, isOpen: boolean) => {
            dispatch(
                updateFilterVisibility({
                    filterID: id,
                    isOpen: isOpen,
                }),
            );
        };

        const onSelectOneChange = (
            optionNamespace: string,
            filterID: string,
            optionID: string | null,
        ) => {
            dispatch(
                updateSelectOneFilterValue({
                    optionNamespace: optionNamespace,
                    filterID: filterID,
                    optionID: optionID,
                }),
            );
        };

        const onSelectManyChange = (
            optionNamespace: string,
            filterID: string,
            optionID: string,
            isSelected: boolean,
        ) => {
            dispatch(
                updateSelectManyFilterValue({
                    optionNamespace: optionNamespace,
                    filterID: filterID,
                    optionID: optionID,
                    isSelected: isSelected,
                }),
            );
        };

        const onTitleEvent = (
            event:
                | React.MouseEvent<HTMLElement>
                | React.KeyboardEvent<HTMLElement>,
        ) => {
            event.preventDefault();
            onSetVisibility(visibilityID, !isOpen);
        };

        const onKeyPressHandler = (event: React.KeyboardEvent<HTMLElement>) => {
            if (event.key === Key.Enter) {
                onTitleEvent(event);
            }
        };

        useMountEffect(() => {
            const currentBreakpoint = getViewportBreakpoint();
            if (currentBreakpoint <= BreakPoint.MEDIUM) {
                onSetVisibility(visibilityID, false);
            }
        });

        const containerClasses = classNames({
            [styles.filterContainer]: true,
        });
        const titleClasses = classNames({
            [styles.filterTitle]: true,
            [styles.filterTitleOpen]: isOpen,
            [styles.filterTitleClose]: !isOpen,
            "product-grid-filter__header": true,
        });
        const bodyClasses = classNames({
            [styles.filterBody]: true,
            "product-grid-filter__body--open": isOpen,
            [styles.filterBodyClose]: !isOpen,
            "product-grid-filter__body--select-many":
                props.config.selectionMode ===
                GridFilterSelectionMode.SELECT_MANY,
            "product-grid-filter__body--select-one":
                props.config.selectionMode ===
                GridFilterSelectionMode.SELECT_ONE,
        });
        return (
            <fieldset className={containerClasses}>
                <legend
                    className={titleClasses}
                    onClick={onTitleEvent}
                    role="button"
                    tabIndex={0}
                    onKeyPress={onKeyPressHandler}
                    aria-controls={bodyId}
                    aria-expanded={isOpen}
                >
                    {props.config.label}
                    {props.showSelectedList && (
                        <span className={styles.selectedFiltersList}>
                            {selectedValuesLabels.join(", ")}
                        </span>
                    )}
                </legend>

                <div className={bodyClasses} id={bodyId} ref={filterRef}>
                    {buildFilterBody(
                        props.config,
                        products,
                        selectedValues,
                        onSelectOneChange,
                        onSelectManyChange,
                    )}
                </div>
            </fieldset>
        );
    },
);
